/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Spin, Button, Input, Form } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadBookingStatusReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const BookingStatusReport = () => {
  // Set default date range to last 7 days
  const defaultDateRange = [
    moment().subtract(1, "days"),
    moment()
  ];

  const [loading, setLoading] = useState(false);
  const [hubListLoading, setHubListLoading] = useState(false);
  const [cpListLoading, setCpListLoading] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [cpList, setCpList] = useState([]);
  const [selectedCps, setSelectedCps] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

  const [awbInput, setAwbInput] = useState("");
  const [awbFilter, setAwbFilter] = useState("");
  const awbInputRef = useRef(null);
  // Request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  const [headerHubList, setHeaderHubList] = useState([]);

  // Fetch hubs from header/localStorage
  useEffect(() => {
    try {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];
      if (userHubs?.length > 0) {
        const headerHubs = [
          { label: "All", value: "ALL" },
          ...userHubs.map(hub => ({
            label: hub.hubName,
            value: hub.hubId
          }))
        ];
        setHeaderHubList(headerHubs);
      } else {
        // If no hubs assigned, show all available hubs but without "All" option
        setHeaderHubList([]);
      }
    } catch (e) {
      console.error("Error parsing user hubs for Booking Status Report", e);
      setHeaderHubList([]);
    }
  }, []);

  const handleMultiSelectChange = (values, setter, options = []) => {
    if (values.includes("ALL")) {
      const isLatestAll = values[values.length - 1] === "ALL";
      if (isLatestAll) {
        // Select all actual values (excluding the "ALL" option itself)
        const allActualValues = options
          .filter(opt => opt.value !== "ALL")
          .map(opt => opt.value);
        setter(allActualValues);
      } else {
        // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
        setter(values.filter(v => v !== "ALL"));
      }
    } else {
      setter(values);
    }
  };

  // Ref to track whether we already set the hub for the current login session
  const hubInitializedRef = useRef(false);

  useEffect(() => {
    const handleHubChanged = () => {
      const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
      const newActiveHubId = activeHub?.hubId;

      if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([newActiveHubId]);
      } else {
        setSelectedHubs(["ALL"]);
      }
    };

    window.addEventListener("hubChanged", handleHubChanged);
    return () => window.removeEventListener("hubChanged", handleHubChanged);
  }, []);

  // Set default hub based on logged-in user — always enforced for hub-restricted roles
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const role = user?.roles?.[0];
    setHubUser(role);

    const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
    const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";
    if (!hubInitializedRef.current) {
      const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
      const userHubs = userFullData?.userInfo?.hubDetails || [];

      if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
        setSelectedHubs([currentActiveHubId]);
      } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
        setSelectedHubs([]);
      } else {
        setSelectedHubs(["ALL"]);
      }
      hubInitializedRef.current = true;
    }
  }, [headerHubList]); // Depend on headerHubList to ensure we have options available

  // Fetch hub list on mount
  useEffect(() => {
    (async () => {
      try {
        setHubListLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error("Failed to fetch hub list");
      } finally {
        setHubListLoading(false);
      }
    })();
  }, []);

  // Fetch CP list when hubs change
  useEffect(() => {
    if (selectedHubs.length > 0) {
      setCpListLoading(true);
      Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
        .then(cpLists => {
          const allCps = [...new Set(cpLists.flat())];
          setCpList(allCps);
        })
        .catch(() => {
          message.error("Failed to fetch CP lists");
          setCpList([]);
        })
        .finally(() => {
          setCpListLoading(false);
        });
    } else {
      setCpList([]);
      setCpListLoading(false);
    }
  }, [selectedHubs]);

  // Fetch report data when filters or pagination change
  useEffect(() => {
    // Wait for hubUser to be initialized
    if (!hubUser) {
      return;
    }

    // For HUBADMIN users, hub selection is mandatory
    if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
      return; // Don't fetch if HUBADMIN has no hub selected
    }

    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedHubs, selectedCps, dateRange, awbFilter, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    // Create new request with cancellation support
    const { signal, requestId } = createNewRequest("booking-status-report");

    setLoading(true);
    try {
      // For HUBADMIN users, hub selection is mandatory
      if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
        return;
      }

      // Resolve Hubs (selectedHubs) for API logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      // Prepare params according to API requirements
      const params = {
        hubNames: selectedHubData.map(hub => hub.label),
        cpNames: selectedCpData.map(cp => cp.label),
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
        awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean),
        page: pagination.current,
        pageSize: pagination.pageSize
      };

      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getBookingStatusReport(params, { signal });

      // Check if this is still the latest request
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring response");
        return;
      }

      setReportData(data);
      setLastsynced(lastRefreshedAt);
      setNextScheduledRefresh(nextScheduledRefresh);
      setPagination(prev => ({
        ...prev,
        total,
        current: page,
        pageSize
      }));
    } catch (error) {
      // Check if this is still the latest request
      if (!isLatestRequest(requestId)) {
        console.log("🔥 Request cancelled - ignoring error");
        return;
      }

      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled");
        return;
      }

      console.error("Error fetching report data:", error);
      message.error("Failed to fetch report data");
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      // Only update loading state if this is still the latest request
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    try {
      // Validate required fields
      if (!dateRange[0] || !dateRange[1]) {
        message.error("Please select date range for download");
        return;
      }

      /*if (selectedHubs.length === 0) {
        message.error("Hub selection is required");
        return;
      }*/

      // Resolve Hubs (selectedHubs) for Download logic
      const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
      const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
      const resolvedHubIds = selectedHubs?.includes("ALL")
        ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
        : selectedHubs;

      const selectedHubData = hubList.filter(hub => resolvedHubIds?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const downloadParams = {
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        fromHubs: selectedHubData.map(hub => hub.label),
        fromCPs: selectedCpData.map(cp => cp.label),
        awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean),
      };

      await downloadBookingStatusReport(downloadParams);
      message.success("Download started successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to start download");
    }
  };

  // Table columns (customize as needed)
  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      sorter: true,
      width: 70,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Shipment No", dataIndex: "shipmentNo", key: "shipmentNo", sorter: true, width: 140,
      render: (text) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    { title: "Booking Datetime", dataIndex: "bookingDatetime", key: "bookingDatetime", sorter: true, width: 140 },
    { title: "From Center", dataIndex: "fromCenter", key: "fromCenter", sorter: true, width: 120 },
    { title: "From Hub", dataIndex: "fromHub", key: "fromHub", sorter: true, width: 160 },
    { title: "From CP", dataIndex: "fromCP", key: "fromCP", sorter: true, width: 160 },
    { title: "Current Hub", dataIndex: "currentPremiseName", key: "currentPremiseName", sorter: true, width: 160 },
    { title: "Current Status", dataIndex: "currentStatus", key: "currentStatus", sorter: true, width: 120 },
    { title: "Current Status Time", dataIndex: "currentStatusTime", key: "currentStatusTime", sorter: true, width: 150 },
    { title: "Audit Status", dataIndex: "status", key: "status", sorter: true, width: 120 },
    { title: "From Pincode", dataIndex: "fromPincode", key: "fromPincode", sorter: true, width: 120 },
    { title: "Audited Weight (kg)", dataIndex: "auditedWeight", key: "auditedWeight", sorter: true, width: 120 },
    { title: "Product Type", dataIndex: "productType", key: "productType", sorter: true, width: 120 },
    { title: "Booking Type", dataIndex: "bookingType", key: "bookingType", sorter: true, width: 120 },
    { title: "Transport Mode", dataIndex: "transportMode", key: "transportMode", sorter: true, width: 120 },
    { title: "To Hub", dataIndex: "toHub", key: "toHub", sorter: true, width: 160 },
    { title: "To Center", dataIndex: "toCenter", key: "toCenter", sorter: true, width: 120 },
    { title: "To Pincode", dataIndex: "toPincode", key: "toPincode", sorter: true, width: 120 },
  ];

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Booking Data vs Status Report" newReportUrl="/reports/booking-data-vs-status/">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={4}>
            <div className="filter-label">Date Range</div>
            <CalendarComponent
              type="range"
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div className="filter-label">From Hub</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Select Hubs"
              options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
              onChange={(val) => {
                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedHubs}
              allowClear
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
              loading={hubListLoading}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <div className="filter-label">From CP</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="From CP"
              options={cpList}
              onChange={setSelectedCps}
              value={selectedCps}
              allowClear
              style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
              mode="multiple"
              maxTagCount="responsive"
              disabled={selectedHubs.length === 0}
              loading={cpListLoading}
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">AWB Number</div>
            <Form.Item style={{ marginBottom: 0 }}>
              <Input
                ref={awbInputRef}
                placeholder="Search AWB record..."
                value={awbInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setAwbInput(value);
                  if (!value) {
                    setAwbFilter("");
                  }
                }}
                onPressEnter={() => setAwbFilter(awbInput)}
                onClear={() => {
                  setAwbInput("");
                  setAwbFilter("");
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData("text");
                  // Split by newlines, tabs, or multiple spaces and filter empty strings
                  const processedText = pastedText
                    .split(/[\r\n\t ]+/)
                    .map(item => item.trim())
                    .filter(item => item !== "")
                    .join(",");

                  if (processedText) {
                    // If there's already text, append with a comma if needed
                    const newValue = awbInput
                      ? `${awbInput.trim().replace(/,+$/, "")},${processedText}`
                      : processedText;
                    setAwbInput(newValue);

                    // Scroll to end after state update
                    setTimeout(() => {
                      if (awbInputRef.current) {
                        const inputElement = awbInputRef.current.input;
                        if (inputElement) {
                          inputElement.scrollLeft = inputElement.scrollWidth;
                          // Also set cursor to end
                          const len = newValue.length;
                          inputElement.setSelectionRange(len, len);
                        }
                      }
                    }, 0);
                  }
                }}
                style={{
                  height: "32px",
                  maxHeight: "32px",
                  borderRadius: "5px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#fff",
                  overflow: "hidden"
                }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4} style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div className="filter-label">&nbsp;</div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              loading={loading}
              disabled={!dateRange[0] || !dateRange[1]}
              style={{ width: "100%", height: "32px", borderRadius: "5px" }}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <Spin spinning={loading}>
        <TableComponent
          columns={columns}
          dataSource={reportData}
          loading={false} // Set to false since we're using Spin wrapper
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
          sticky
        />
      </Spin>
    </div>
  );
};

export default BookingStatusReport; 
